/**
 * VaultSetup page -- guides the patient through creating their encrypted vault.
 *
 * Step 1: Explain what the vault does
 * Step 2: Generate key, display seed phrase for backup
 * Step 3: Optionally register biometric access
 * Step 4: Confirm vault is ready with a test encryption round-trip
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePatientVault } from '@/hooks/use-patient-vault';
import {
  Shield,
  Key,
  Fingerprint,
  CheckCircle,
  Copy,
  ArrowRight,
  ArrowLeft,
  Lock,
  AlertTriangle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STEPS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'create', label: 'Create Vault' },
  { id: 'biometric', label: 'Biometric' },
  { id: 'ready', label: 'Vault Ready' },
];

const VaultSetup: React.FC = () => {
  const navigate = useNavigate();
  const vault = usePatientVault();

  const [step, setStep] = useState(0);
  const [localSeed, setLocalSeed] = useState<string | null>(null);
  const [seedCopied, setSeedCopied] = useState(false);
  const [seedRevealed, setSeedRevealed] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [testPassed, setTestPassed] = useState(false);
  const [isTestRunning, setIsTestRunning] = useState(false);

  // Step 2: create the vault
  const handleCreateVault = useCallback(async () => {
    setIsCreating(true);
    try {
      const phrase = await vault.initializeVault();
      setLocalSeed(phrase);
      setStep(1);
    } catch {
      // error is surfaced via vault.error
    } finally {
      setIsCreating(false);
    }
  }, [vault]);

  // Copy seed to clipboard
  const handleCopySeed = useCallback(async () => {
    if (!localSeed) return;
    try {
      await navigator.clipboard.writeText(localSeed);
      setSeedCopied(true);
      setTimeout(() => setSeedCopied(false), 3000);
    } catch {
      // Clipboard API might not be available
    }
  }, [localSeed]);

  // Step 3: register biometric
  const handleRegisterBiometric = useCallback(async () => {
    await vault.biometric.register();
  }, [vault.biometric]);

  // Step 4: test encryption round-trip
  const handleRunTest = useCallback(async () => {
    setIsTestRunning(true);
    try {
      const testRecord = {
        id: '__vault-test__',
        type: 'system-test',
        title: 'Vault Self-Test',
        data: { message: 'If you can read this, encryption works.', ts: Date.now() },
      };
      await vault.storeHealthRecord(testRecord);
      const decrypted = await vault.getHealthRecord('__vault-test__');
      if (decrypted && typeof decrypted === 'object' && 'data' in (decrypted as Record<string, unknown>)) {
        setTestPassed(true);
      }
      // Clean up test record
      await vault.deleteHealthRecord('__vault-test__');
    } catch {
      setTestPassed(false);
    } finally {
      setIsTestRunning(false);
    }
  }, [vault]);

  const canProceedFromSeed = localSeed && seedCopied;

  return (
    <PageContainer
      title="Set Up Health Vault"
      description="Create an encrypted vault to store your health records securely on this device."
      icon={<Shield />}
    >
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-colors',
                i <= step
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'h-0.5 w-8 transition-colors',
                  i < step ? 'bg-primary' : 'bg-muted',
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Error banner */}
      {vault.error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Something went wrong</p>
            <p className="text-sm mt-1">{vault.error}</p>
          </div>
        </div>
      )}

      {/* ==================== Step 0: Introduction ==================== */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Secure Your Health Data
            </CardTitle>
            <CardDescription>
              Your health records deserve the highest level of protection.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <Lock className="h-6 w-6 text-primary" />
                <h4 className="font-semibold text-sm">Encrypted on Device</h4>
                <p className="text-xs text-muted-foreground">
                  All records are encrypted with AES-256 before storage.
                  Data never leaves your device unencrypted.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <Key className="h-6 w-6 text-primary" />
                <h4 className="font-semibold text-sm">Your Key, Your Data</h4>
                <p className="text-xs text-muted-foreground">
                  A private encryption key is generated on this device.
                  Only you can decrypt your records.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <Fingerprint className="h-6 w-6 text-primary" />
                <h4 className="font-semibold text-sm">Biometric Access</h4>
                <p className="text-xs text-muted-foreground">
                  Use your fingerprint or face to unlock the vault.
                  No passwords to remember for daily use.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleCreateVault} disabled={isCreating}>
                {isCreating ? 'Creating Vault...' : 'Create My Vault'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== Step 1: Seed Phrase ==================== */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Your Recovery Phrase
            </CardTitle>
            <CardDescription>
              Write down these 12 words and store them somewhere safe. This is the
              only way to recover your vault if you lose access to this device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Do not share this phrase with anyone.</strong> Anyone with these
                words can decrypt your health records. Store them offline in a safe place.
              </p>
            </div>

            {/* Seed phrase display */}
            <div className="relative">
              <div
                className={cn(
                  'grid grid-cols-3 gap-2 p-4 rounded-lg border bg-muted/30 transition-all',
                  !seedRevealed && 'blur-md select-none',
                )}
                aria-hidden={!seedRevealed}
              >
                {localSeed?.split(' ').map((word, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded bg-background border text-sm"
                  >
                    <span className="text-muted-foreground font-mono text-xs w-5 text-right">
                      {i + 1}.
                    </span>
                    <span className="font-medium">{word}</span>
                  </div>
                ))}
              </div>

              {!seedRevealed && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setSeedRevealed(true)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Reveal Recovery Phrase
                  </Button>
                </div>
              )}
            </div>

            {seedRevealed && (
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleCopySeed}>
                  <Copy className="mr-2 h-4 w-4" />
                  {seedCopied ? 'Copied!' : 'Copy to Clipboard'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSeedRevealed(false)}
                >
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide
                </Button>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceedFromSeed}
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {!canProceedFromSeed && seedRevealed && (
              <p className="text-xs text-muted-foreground text-center">
                Please copy your recovery phrase before continuing.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ==================== Step 2: Biometric ==================== */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-primary" />
              Set Up Biometric Access
            </CardTitle>
            <CardDescription>
              Use your fingerprint or face to quickly unlock your vault.
              This step is optional but recommended.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {vault.biometric.isSupported ? (
              <div className="space-y-4">
                <div className="p-6 rounded-lg border bg-muted/30 flex flex-col items-center gap-4">
                  <Fingerprint className="h-16 w-16 text-primary/60" />
                  {vault.biometric.isRegistered ? (
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Biometric registered successfully</span>
                    </div>
                  ) : (
                    <Button onClick={handleRegisterBiometric}>
                      <Fingerprint className="mr-2 h-4 w-4" />
                      Register Biometric
                    </Button>
                  )}
                </div>

                {vault.biometric.error && (
                  <p className="text-sm text-destructive">{vault.biometric.error}</p>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-muted/50 text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Biometric authentication is not available on this device.
                  You can use your recovery phrase to unlock the vault instead.
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => setStep(3)}>
                {vault.biometric.isRegistered ? 'Continue' : 'Skip for Now'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== Step 3: Ready ==================== */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              Vault Ready
            </CardTitle>
            <CardDescription>
              Your encrypted health vault has been created. Run a quick self-test
              to verify everything works correctly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="p-4 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-sm text-green-800 dark:text-green-200">
                    Encryption Key
                  </span>
                </div>
                <p className="text-xs text-green-700 dark:text-green-300">
                  AES-GCM-256 key generated and stored
                </p>
              </div>

              <div className="p-4 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-sm text-green-800 dark:text-green-200">
                    Recovery Phrase
                  </span>
                </div>
                <p className="text-xs text-green-700 dark:text-green-300">
                  12-word backup phrase saved
                </p>
              </div>

              <div
                className={cn(
                  'p-4 rounded-lg border',
                  vault.biometric.isRegistered
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                    : 'bg-muted/50 border-border',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  {vault.biometric.isRegistered ? (
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Fingerprint className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span
                    className={cn(
                      'font-medium text-sm',
                      vault.biometric.isRegistered
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-muted-foreground',
                    )}
                  >
                    Biometric Access
                  </span>
                </div>
                <p
                  className={cn(
                    'text-xs',
                    vault.biometric.isRegistered
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-muted-foreground',
                  )}
                >
                  {vault.biometric.isRegistered ? 'Registered' : 'Skipped'}
                </p>
              </div>

              <div
                className={cn(
                  'p-4 rounded-lg border',
                  testPassed
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                    : 'bg-muted/50 border-border',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  {testPassed ? (
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Shield className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span
                    className={cn(
                      'font-medium text-sm',
                      testPassed
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-muted-foreground',
                    )}
                  >
                    Self-Test
                  </span>
                </div>
                <p
                  className={cn(
                    'text-xs',
                    testPassed
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-muted-foreground',
                  )}
                >
                  {testPassed ? 'Encrypt/decrypt verified' : 'Not run yet'}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
              {!testPassed && (
                <Button variant="outline" onClick={handleRunTest} disabled={isTestRunning}>
                  <Shield className="mr-2 h-4 w-4" />
                  {isTestRunning ? 'Testing...' : 'Run Self-Test'}
                </Button>
              )}

              <Button onClick={() => navigate('/vault')}>
                <Lock className="mr-2 h-4 w-4" />
                Go to Health Vault
              </Button>
            </div>

            <div className="flex justify-start">
              <Button variant="ghost" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
};

export default VaultSetup;
